import { useState, useRef, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { Pencil, Check, X, GitFork, Plus, ChevronLeft, BookOpen, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  useTemplate, useUpdateTemplate,
  useCreateModule, useDeleteModule,
  useCreateTopic, useDeleteTopic,
  useUpdateModule, useUpdateTopic,
  useForkTemplate,
} from '@/lib/hooks/useCurriculum';
import { adminApi } from '@/lib/api/admin';
import { ModuleListItem } from './ModuleListItem';
import { ModuleDetail } from './ModuleDetail';
import { ContentSheet } from '@/features/Trainer/ContentSheet';
import { ModuleForm } from '@/features/Trainer/ModuleForm';
import { TopicForm } from '@/features/Trainer/TopicForm';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';
import type { CurriculumTopic } from '@/lib/api/curriculum';


interface TemplateEditorProps {
  templateId: string;
}

export function TemplateEditor({ templateId }: TemplateEditorProps) {
  const { data: template, isLoading } = useTemplate(templateId);
  const updateTemplate = useUpdateTemplate(templateId);
  const createModule = useCreateModule();
  const deleteModule = useDeleteModule();
  const updateModule = useUpdateModule();
  const createTopic = useCreateTopic();
  const updateTopic = useUpdateTopic();
  const deleteTopic = useDeleteTopic();
  const forkTemplate = useForkTemplate();

  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [activeTopic, setActiveTopic] = useState<{ id: string; moduleId: string } | null>(null);
  const [panelWidth, setPanelWidth] = useState(260);
  const dragging = useRef(false);

  type SheetMode = { kind: 'add-module' } | { kind: 'add-topic'; moduleId: string } | null;
  const [sheet, setSheet] = useState<SheetMode>(null);

  // Module drag-and-drop
  const dragModuleIdx = useRef<number | null>(null);
  const [dragOverModuleIdx, setDragOverModuleIdx] = useState<number | null>(null);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    const startX = e.clientX;
    const startW = panelWidth;
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      const next = Math.min(480, Math.max(160, startW + ev.clientX - startX));
      setPanelWidth(next);
    };
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  // Header editing
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');

  // Fork modal
  const [showForkModal, setShowForkModal] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState('');

  const { data: allBatches = [] } = useQuery({
    queryKey: ['admin', 'batches'],
    queryFn: () => adminApi.listBatches(),
    enabled: showForkModal,
  });

  if (isLoading || !template) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const modules = [...(template.modules ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const totalTopics = modules.reduce((sum, m) => sum + (m.topics?.length ?? 0), 0);
  const totalHours = modules.reduce(
    (sum, m) => sum + (m.topics ?? []).reduce((s, t) => s + (t.estimated_hours ?? 0), 0), 0
  );

  const effectiveId = selectedModuleId ?? modules[0]?.id ?? null;
  const selectedModule = modules.find((m) => m.id === effectiveId) ?? null;

  const handleSaveTitle = () => {
    if (!editTitle.trim()) return;
    updateTemplate.mutate({ title: editTitle.trim() }, {
      onSuccess: () => setEditingTitle(false),
    });
  };

  const handlePublish = () => {
    updateTemplate.mutate({ status: template.status === 'published' ? 'draft' : 'published' });
  };

  const handleDeleteModule = (id: string) => {
    deleteModule.mutate(id, {
      onSuccess: () => {
        if (effectiveId === id) setSelectedModuleId(null);
        if (activeTopic?.moduleId === id) setActiveTopic(null);
      },
    });
  };

  // Find the currently active topic (for the right-panel topic detail view)
  const activeTopicData: CurriculumTopic | null = activeTopic
    ? (modules.find((m) => m.id === activeTopic.moduleId)?.topics?.find((t) => t.id === activeTopic.id) ?? null)
    : null;

  const handleModuleDrop = (dropIdx: number) => {
    const fromIdx = dragModuleIdx.current;
    setDragOverModuleIdx(null);
    dragModuleIdx.current = null;
    if (fromIdx === null || fromIdx === dropIdx) return;
    const reordered = [...modules];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    reordered.forEach((m, i) => {
      if (m.sort_order !== i + 1) updateModule.mutate({ id: m.id, data: { sort_order: i + 1 } });
    });
  };

  const handleFork = () => {
    if (!selectedBatchId) return;
    forkTemplate.mutate({ templateId, batchId: selectedBatchId }, {
      onSuccess: () => {
        toast.success('Template forked to batch');
        setShowForkModal(false);
        setSelectedBatchId('');
      },
      onError: () => {
        toast.error('Fork failed — batch may already have a curriculum');
      },
    });
  };


  return (
    <div className="-m-6 flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 52px)' }}>
      {/* Page header */}
      <div className="flex flex-shrink-0 items-center gap-4 border-b border-border bg-background px-6 py-3">
        <div className="flex flex-1 items-center gap-3 min-w-0">
          <Link
            to="/admin/curriculum"
            className="text-sm text-muted-foreground hover:text-foreground shrink-0"
          >
            Curriculum
          </Link>
          <span className="text-muted-foreground/50 shrink-0">/</span>

          {editingTitle ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                className="flex-1 min-w-0 rounded border border-border bg-background px-2 py-0.5 text-sm font-semibold"
                autoFocus
              />
              <button onClick={handleSaveTitle} className="text-green-600 shrink-0" title="Save title">
                <Check className="h-4 w-4" />
              </button>
              <button onClick={() => setEditingTitle(false)} className="text-muted-foreground shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="group flex items-center gap-1.5 min-w-0">
              <span className="truncate text-sm font-semibold">{template.title}</span>
              <button
                onClick={() => { setEditTitle(template.title); setEditingTitle(true); }}
                className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                title="Edit title"
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-muted-foreground hidden sm:inline">
            {modules.length}m &middot; {totalTopics}t &middot; {totalHours.toFixed(0)}h
          </span>
          <span className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            template.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800',
          )}>
            {template.status}
          </span>
          {template.status === 'published' && (
            <button
              onClick={() => { setShowForkModal(true); setSelectedBatchId(''); }}
              className="flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <GitFork className="h-3.5 w-3.5" /> Fork to Batch
            </button>
          )}
          <button
            onClick={handlePublish}
            disabled={updateTemplate.isPending}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50',
              template.status === 'published'
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                : 'bg-green-600 text-white hover:bg-green-700',
            )}
          >
            {template.status === 'published' ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </div>

      {/* Split pane */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — module navigator */}
        <div
          className="flex flex-shrink-0 flex-col overflow-hidden border-r border-border bg-muted/20"
          style={{ width: panelWidth }}
        >
          <div className="flex-1 overflow-y-auto py-2">
            {modules.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-muted-foreground">
                No modules yet.
              </p>
            ) : (() => {
              let lastPhase: string | null = null;
              return modules.map((mod, idx) => {
                const showHeader = mod.phase !== lastPhase;
                lastPhase = mod.phase;
                return (
                  <div key={mod.id}>
                    {showHeader && (
                      <p className="mb-0.5 mt-1 px-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/60">
                        {mod.phase}
                      </p>
                    )}
                    <div
                      draggable
                      onDragStart={() => { dragModuleIdx.current = idx; }}
                      onDragOver={(e) => { e.preventDefault(); setDragOverModuleIdx(idx); }}
                      onDragLeave={() => setDragOverModuleIdx(null)}
                      onDrop={() => handleModuleDrop(idx)}
                      className={cn(
                        'rounded transition-colors',
                        dragOverModuleIdx === idx && dragModuleIdx.current !== idx
                          ? 'bg-primary/10 outline outline-1 outline-primary/50'
                          : '',
                      )}
                    >
                      <ModuleListItem
                        module={mod}
                        selected={mod.id === effectiveId}
                        onClick={() => { setSelectedModuleId(mod.id); setActiveTopic(null); }}
                        onUpdateModule={(id, data) => updateModule.mutate({ id, data })}
                        onDeleteModule={handleDeleteModule}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Add module */}
          <div className="border-t border-border p-2">
            <button
              type="button"
              onClick={() => setSheet({ kind: 'add-module' })}
              className="flex w-full items-center justify-center gap-1.5 rounded-md py-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" /> Add Module
            </button>
          </div>
        </div>

        {/* Resize handle */}
        <div
          onMouseDown={startResize}
          className="w-1 flex-shrink-0 cursor-col-resize bg-transparent hover:bg-primary/30 active:bg-primary/50 transition-colors"
        />

        {/* Right panel — module detail or topic detail */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {activeTopicData && activeTopic ? (
            <TopicDetailPanel
              key={activeTopicData.id}
              topic={activeTopicData}
              onUpdate={(data) => updateTopic.mutate({ id: activeTopic.id, data })}
              onDelete={() => deleteTopic.mutate(activeTopic.id, { onSuccess: () => setActiveTopic(null) })}
              onClose={() => setActiveTopic(null)}
            />
          ) : selectedModule ? (
            <ModuleDetail
              key={selectedModule.id}
              module={selectedModule}
              onUpdateModule={(id, data) => updateModule.mutate({ id, data })}
              onDeleteModule={handleDeleteModule}
              onUpdateTopic={(id, data) => updateTopic.mutate({ id, data })}
              onDeleteTopic={(id) => deleteTopic.mutate(id)}
              onAddTopic={() => setSheet({ kind: 'add-topic', moduleId: selectedModule.id })}
              onTopicClick={(topicId) => setActiveTopic({ id: topicId, moduleId: selectedModule.id })}
              selectedTopicId={activeTopic?.id}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-muted-foreground">
                {modules.length === 0 ? 'Add a module to get started.' : 'Select a module to view details.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Fork modal */}
      {showForkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Fork to Batch</h2>
              <button onClick={() => setShowForkModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
              Creates a copy of <strong>{template.title}</strong> for the selected batch.
              Topics and modules will be editable per-batch after forking.
            </p>
            <label className="mb-1 block text-sm font-medium">Select batch</label>
            <select
              value={selectedBatchId}
              onChange={(e) => setSelectedBatchId(e.target.value)}
              className="mb-4 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">— choose a batch —</option>
              {allBatches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.org_name})
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowForkModal(false)}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleFork}
                disabled={!selectedBatchId || forkTemplate.isPending}
                className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
              >
                <GitFork className="h-4 w-4" />
                {forkTemplate.isPending ? 'Forking…' : 'Fork'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add module sheet */}
      <ContentSheet
        open={sheet?.kind === 'add-module'}
        title="Add module"
        onClose={() => setSheet(null)}
      >
        <ModuleForm
          onSave={async (req) => {
            await new Promise<void>((resolve, reject) => {
              createModule.mutate({
                template_id: templateId,
                title: req.title,
                sort_order: modules.length + 1,
                phase: req.phase || 'fundamentals',
                estimated_days: req.estimated_days,
              }, {
                onSuccess: (res) => {
                  setSelectedModuleId(res.data.id);
                  setActiveTopic(null);
                  setSheet(null);
                  resolve();
                },
                onError: (err) => reject(err),
              });
            });
          }}
          onCancel={() => setSheet(null)}
        />
      </ContentSheet>

      {/* Add topic sheet */}
      <ContentSheet
        open={sheet?.kind === 'add-topic'}
        title="Add topic"
        onClose={() => setSheet(null)}
      >
        {sheet?.kind === 'add-topic' && (
          <TopicForm
            onSave={async (req) => {
              const mod = modules.find((m) => m.id === sheet.moduleId);
              await new Promise<void>((resolve, reject) => {
                createTopic.mutate({
                  module_id: sheet.moduleId,
                  title: req.title,
                  sort_order: (mod?.topics?.length ?? 0) + 1,
                  topic_type: req.topic_type,
                  estimated_hours: req.estimated_hours,
                }, {
                  onSuccess: () => { setSheet(null); resolve(); },
                  onError: (err) => reject(err),
                });
              });
            }}
            onCancel={() => setSheet(null)}
          />
        )}
      </ContentSheet>
    </div>
  );
}

// --- TopicDetailPanel ---------------------------------------------------

interface TopicDetailPanelProps {
  topic: CurriculumTopic;
  onUpdate: (data: { title?: string; topic_type?: string; estimated_hours?: number }) => void;
  onDelete: () => void;
  onClose: () => void;
}

const topicTypeColors: Record<string, string> = {
  lecture: 'bg-blue-50 text-blue-700',
  lab: 'bg-amber-50 text-amber-700',
  project: 'bg-purple-50 text-purple-700',
};

function TopicDetailPanel({ topic, onUpdate, onDelete, onClose }: TopicDetailPanelProps) {
  const [title, setTitle] = useState(topic.title);
  const [topicType, setTopicType] = useState(topic.topic_type);
  const [hours, setHours] = useState(topic.estimated_hours?.toString() ?? '');
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleSave = () => {
    if (!title.trim()) return;
    onUpdate({
      title: title.trim(),
      topic_type: topicType,
      estimated_hours: hours ? parseFloat(hours) : undefined,
    });
    setDirty(false);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-border px-6 py-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label="Back to module"
          title="Back to module"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <h2 className="flex-1 truncate text-base font-semibold">{topic.title}</h2>
        <span className={cn('rounded px-2 py-0.5 text-xs font-medium', topicTypeColors[topic.topic_type])}>
          {topic.topic_type}
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-md space-y-4 p-6">
          <div>
            <label htmlFor="topic-title" className="mb-1 block text-sm font-medium">Title</label>
            <input
              id="topic-title"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label htmlFor="topic-type" className="mb-1 block text-sm font-medium">Type</label>
            <select
              id="topic-type"
              value={topicType}
              onChange={(e) => {
                setTopicType(e.target.value as CurriculumTopic['topic_type']);
                setDirty(true);
              }}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="lecture">Lecture</option>
              <option value="lab">Lab</option>
              <option value="project">Project</option>
            </select>
          </div>

          <div>
            <label htmlFor="topic-hours" className="mb-1 block text-sm font-medium">Estimated hours</label>
            <input
              id="topic-hours"
              type="number"
              min="0.25"
              step="0.25"
              value={hours}
              onChange={(e) => { setHours(e.target.value); setDirty(true); }}
              placeholder="Optional"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          {dirty && (
            <button
              type="button"
              onClick={handleSave}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Save Changes
            </button>
          )}

          {/* Info section */}
          <div className="mt-4 flex gap-3 rounded-lg border border-border bg-muted/30 p-4">
            <BookOpen className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Sessions, Resources & Assessments are managed in the Batch Curriculum after a template
              is forked to a batch.
            </p>
          </div>

          {/* Danger zone */}
          <div className="mt-8 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
            <p className="mb-2 text-sm font-medium text-destructive">Danger Zone</p>
            <p className="mb-3 text-xs text-muted-foreground">
              Deleting this topic cannot be undone.
            </p>
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-destructive">Are you sure?</span>
                <button
                  type="button"
                  onClick={onDelete}
                  className="rounded bg-destructive px-3 py-1 text-xs text-white"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="rounded border border-border px-3 py-1 text-xs"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 rounded-md border border-destructive/50 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete Topic
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
